import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../App';
import { useAuth } from '../AuthContext';
import { t } from '../i18n';
import LanguageToggle from '../components/game/LanguageToggle';
import Sidebar from '../components/game/Sidebar';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookOpen, Gamepad2, Flame, Coins, Target, Trophy, ShoppingBag,
  Users, Swords, ChevronRight, ChevronDown, Star, Zap, Award,
  CreditCard, HelpCircle, ArrowRight, CheckCircle2, Circle,
  RotateCcw, TrendingUp, Gift, Shield, GraduationCap, HandCoins
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function GuidePage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user, token } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [activeStep, setActiveStep] = useState({});
  const [gData, setGData] = useState(null);
  const isEN = language === 'en';

  React.useEffect(() => {
    if (token) {
      fetch(`${BACKEND_URL}/api/gamification/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data) setGData(data); })
        .catch(() => {});
    }
  }, [token]);

  const toggleSection = (id) => {
    setActiveSection(activeSection === id ? null : id);
  };

  const toggleStep = (sectionId, stepIdx) => {
    const key = `${sectionId}-${stepIdx}`;
    setActiveStep(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const sections = [
    {
      id: 'how-to-play',
      icon: Gamepad2,
      color: '#E85D3A',
      title: isEN ? 'How to Play' : 'Cara Bermain',
      subtitle: isEN ? 'Learn the Tug of War game' : 'Pelajari permainan Tarik Tali',
      steps: [
        {
          icon: '1️⃣',
          title: isEN ? 'Choose a Subject' : 'Pilih Subjek',
          desc: isEN
            ? 'Pick from 8 subjects: Math, Science, BM, English, History, Geography, IT, or Biology. Each has its own tug of war game!'
            : 'Pilih daripada 8 subjek: Matematik, Sains, BM, BI, Sejarah, Geografi, IT, atau Biologi. Setiap satu ada permainan tarik tali sendiri!',
          tip: isEN ? 'Free plan: 2 subjects. Upgrade for all 8!' : 'Pelan Percuma: 2 subjek. Naik taraf untuk semua 8!',
        },
        {
          icon: '2️⃣',
          title: isEN ? 'Set Up Your Match' : 'Tetapkan Perlawanan',
          desc: isEN
            ? 'Enter team names, choose difficulty (Easy/Medium/Hard), select math operations, and set the round time (30-120 seconds).'
            : 'Masukkan nama pasukan, pilih tahap (Mudah/Sederhana/Sukar), pilih operasi matematik, dan tetapkan masa pusingan (30-120 saat).',
          tip: isEN ? 'Two players share one screen — one on each side!' : 'Dua pemain kongsi satu skrin — satu di setiap sisi!',
        },
        {
          icon: '3️⃣',
          title: isEN ? 'Answer Questions' : 'Jawab Soalan',
          desc: isEN
            ? 'Math: Type the answer using the keypad. Other subjects: Tap the correct MCQ option. Correct answers pull the rope to your side!'
            : 'Matematik: Taip jawapan guna keypad. Subjek lain: Tekan pilihan MCQ yang betul. Jawapan betul tarik tali ke sisi anda!',
          tip: isEN ? 'Get 3+ correct in a row for STREAK bonus points!' : 'Dapat 3+ betul berturut-turut untuk bonus KOMBO!',
        },
        {
          icon: '4️⃣',
          title: isEN ? 'Win the Match' : 'Menang Perlawanan',
          desc: isEN
            ? 'Pull the rope 5 points to your side, OR have the highest score when time runs out. The winner gets more XP and coins!'
            : 'Tarik tali 5 markah ke sisi anda, ATAU dapat markah tertinggi bila masa tamat. Pemenang dapat lebih XP dan syiling!',
          tip: isEN ? 'Hit REMATCH to play again instantly!' : 'Tekan MAIN LAGI untuk main semula!',
        },
      ],
    },
    {
      id: 'gamification',
      icon: Flame,
      color: '#f59e0b',
      title: isEN ? 'Streaks, XP & Levels' : 'Streak, XP & Level',
      subtitle: isEN ? 'Level up and earn rewards' : 'Naik level dan dapat ganjaran',
      steps: [
        {
          icon: '🔥',
          title: isEN ? 'Daily Streak' : 'Streak Harian',
          desc: isEN
            ? 'Play at least 1 game every day to keep your streak alive! Your streak count shows on the Play page. The longer your streak, the more bonus XP you earn.'
            : 'Main sekurang-kurangnya 1 game setiap hari untuk kekalkan streak! Kiraan streak ditunjukkan di halaman Main. Semakin lama streak, semakin banyak bonus XP.',
          tip: isEN ? '7-day streak = 100 bonus coins! 14 days = 250! 30 days = 500!' : 'Streak 7 hari = 100 syiling bonus! 14 hari = 250! 30 hari = 500!',
        },
        {
          icon: '⚡',
          title: isEN ? 'XP (Experience Points)' : 'XP (Mata Pengalaman)',
          desc: isEN
            ? 'Every game gives you XP. Win a match = 100 XP. Lose = 30 XP. Solo vs AI = 50 XP. Complete daily quests for bonus XP too!'
            : 'Setiap permainan beri anda XP. Menang = 100 XP. Kalah = 30 XP. Solo vs AI = 50 XP. Siapkan misi harian untuk bonus XP!',
          tip: isEN ? '100% accuracy = extra 50 XP bonus!' : 'Ketepatan 100% = bonus 50 XP tambahan!',
        },
        {
          icon: '📊',
          title: isEN ? 'Levels & Ranks' : 'Level & Pangkat',
          desc: isEN
            ? 'Earn XP to level up! There are 50 levels and 6 rank tiers: Beginner (1-5), Fighter (6-10), Hero (11-20), Champion (21-30), Legend (31-40), Grandmaster (41-50).'
            : 'Kumpul XP untuk naik level! Ada 50 level dan 6 pangkat: Pemula (1-5), Pejuang (6-10), Wira (11-20), Juara (21-30), Legenda (31-40), Grandmaster (41-50).',
          tip: isEN ? 'Your rank badge shows next to your name everywhere!' : 'Lencana pangkat anda dipapar di sebelah nama anda!',
        },
      ],
    },
    {
      id: 'quests-coins',
      icon: Target,
      color: '#22c55e',
      title: isEN ? 'Daily Quests & Coins' : 'Misi Harian & Syiling',
      subtitle: isEN ? 'Complete missions, earn coins' : 'Siapkan misi, dapat syiling',
      steps: [
        {
          icon: '📋',
          title: isEN ? 'Daily Quests' : 'Misi Harian',
          desc: isEN
            ? 'Every day you get 3 new quests like "Play 1 Match", "Win 1 Match", or "Get 80% Accuracy". Complete them for coin and XP rewards!'
            : 'Setiap hari anda dapat 3 misi baru seperti "Main 1 Perlawanan", "Menang 1 Perlawanan", atau "Dapat 80% Ketepatan". Siapkan untuk ganjaran syiling dan XP!',
          tip: isEN ? 'Quests reset daily — check your Play page every day!' : 'Misi reset setiap hari — semak halaman Main setiap hari!',
        },
        {
          icon: '🪙',
          title: isEN ? 'Earning Coins' : 'Dapat Syiling',
          desc: isEN
            ? 'Win match = 50 coins. Lose = 20 coins. Solo vs AI = 30 coins. Each quest = 20-35 coins. Streak milestones give bonus coins too!'
            : 'Menang = 50 syiling. Kalah = 20 syiling. Solo vs AI = 30 syiling. Setiap misi = 20-35 syiling. Milestone streak beri bonus syiling!',
          tip: isEN ? 'Save up coins for awesome items in the Shop!' : 'Simpan syiling untuk item hebat di Kedai!',
        },
        {
          icon: '🛒',
          title: isEN ? 'The Shop' : 'Kedai',
          desc: isEN
            ? 'Spend coins on cool items: Avatar Frames (show off your style), Rope Skins (customize your game), and Titles (flex your achievements). Items come in Common, Rare, Epic, and Legendary rarity!'
            : 'Belanja syiling untuk item best: Bingkai Avatar (tunjuk gaya), Kulit Tali (customize game), dan Gelaran (tunjuk pencapaian). Item ada rarity Common, Rare, Epic, dan Legendary!',
          tip: isEN ? 'Equip items from your inventory to show them in game!' : 'Pakai item dari inventori untuk tunjuk dalam game!',
        },
      ],
    },
    {
      id: 'leaderboard',
      icon: Trophy,
      color: '#3b82f6',
      title: isEN ? 'Leaderboard & Competition' : 'Papan Markah & Pertandingan',
      subtitle: isEN ? 'Compete and climb the ranks' : 'Bertanding dan panjat ranking',
      steps: [
        {
          icon: '📅',
          title: isEN ? 'Weekly Leaderboard' : 'Papan Markah Mingguan',
          desc: isEN
            ? 'The leaderboard resets every Monday! Everyone starts fresh. Top players get bragging rights. Check the "This Week" tab to see who\'s leading.'
            : 'Papan markah reset setiap Isnin! Semua orang mula dari sifar. Pemain top dapat penghormatan. Semak tab "Minggu Ini" untuk lihat siapa paling hebat.',
          tip: isEN ? 'Even new players can be #1 this week!' : 'Pemain baru pun boleh jadi #1 minggu ini!',
        },
        {
          icon: '🏫',
          title: isEN ? 'School Rankings' : 'Ranking Sekolah',
          desc: isEN
            ? 'Schools compete too! Every game your school plays adds to your school\'s total score. Enter your school name during setup to represent your school.'
            : 'Sekolah juga bertanding! Setiap game sekolah anda main tambah kepada jumlah markah sekolah. Masukkan nama sekolah semasa setup.',
          tip: isEN ? 'Rally your classmates to make your school #1!' : 'Ajak rakan sekelas untuk jadikan sekolah anda #1!',
        },
        {
          icon: '🔄',
          title: isEN ? 'Rematch & Revenge' : 'Main Semula & Balas Dendam',
          desc: isEN
            ? 'Lost a game? Hit the big red REMATCH button to challenge them again with the exact same settings. No need to set up again!'
            : 'Kalah game? Tekan butang MAIN LAGI merah besar untuk cabaran semula dengan tetapan sama. Tak perlu setup semula!',
          tip: isEN ? 'Best of 3? Keep rematching until you win!' : 'Terbaik daripada 3? Terus main sampai menang!',
        },
      ],
    },
    {
      id: 'achievements',
      icon: Award,
      color: '#8b5cf6',
      title: isEN ? 'Achievements & Badges' : 'Pencapaian & Lencana',
      subtitle: isEN ? '19 badges to unlock' : '19 lencana untuk dibuka',
      steps: [
        {
          icon: '🎮',
          title: isEN ? 'Game Achievements' : 'Pencapaian Game',
          desc: isEN
            ? 'First Game, Speed Demon (10+ score), Perfect Round (100% accuracy), Streak Master (5+ combo), Math Warrior (100 correct answers), Champion (10 wins), Gladiator (50 matches).'
            : 'Permainan Pertama, Pejuang Pantas (10+ skor), Pusingan Sempurna (100% ketepatan), Master Kombo (5+ combo), Pejuang Matematik (100 jawapan betul), Juara (10 menang), Gladiator (50 perlawanan).',
          tip: isEN ? 'Check your Progress page to see all achievements!' : 'Semak halaman Kemajuan untuk lihat semua pencapaian!',
        },
        {
          icon: '🔥',
          title: isEN ? 'Streak Achievements' : 'Pencapaian Streak',
          desc: isEN
            ? 'Weekly Warrior (7-day streak), Fortnight Fighter (14 days), Monthly Master (30 days). These are the hardest to get — can you do it?'
            : 'Pejuang Mingguan (streak 7 hari), Pejuang 2 Minggu (14 hari), Master Bulanan (30 hari). Ini paling susah — boleh ke?',
          tip: isEN ? 'Monthly Master gives 500 XP — the biggest achievement reward!' : 'Master Bulanan beri 500 XP — ganjaran pencapaian terbesar!',
        },
        {
          icon: '⭐',
          title: isEN ? 'Special Achievements' : 'Pencapaian Khas',
          desc: isEN
            ? 'Rising Star (Level 10), Veteran (Level 25), Grandmaster (Level 50), Rich Kid (1000 coins), Polymath (all 8 subjects), Shopaholic (buy 5 items), Quest Hunter (10 quests).'
            : 'Bintang Naik (Level 10), Veteran (Level 25), Grandmaster (Level 50), Anak Kaya (1000 syiling), Serba Boleh (semua 8 subjek), Kaki Shopping (beli 5 item), Pemburu Misi (10 misi).',
          tip: isEN ? 'Try to collect all 19 badges!' : 'Cuba kumpul semua 19 lencana!',
        },
      ],
    },
    {
      id: 'rewards',
      icon: Gift,
      color: '#ef4444',
      title: isEN ? 'Reward Ideas for Parents & Teachers' : 'Idea Ganjaran untuk Ibu Bapa & Guru',
      subtitle: isEN ? 'Motivate students with real rewards' : 'Motivasi pelajar dengan ganjaran sebenar',
      steps: [
        {
          icon: '🏠',
          title: isEN ? 'Home Rewards (Parents)' : 'Ganjaran Rumah (Ibu Bapa)',
          desc: isEN
            ? 'Use your child\'s game coins as milestones for real rewards! Ideas: Extra screen time (50 coins), Choose tonight\'s dessert (30), Movie night pick (100), Small toy or sticker pack (200), Pizza party with friends (300), Fun outing to playground or park (500).'
            : 'Gunakan syiling game anak sebagai milestone untuk ganjaran sebenar! Idea: Masa skrin tambahan (50 syiling), Pilih pencuci mulut (30), Pilih filem (100), Mainan kecil atau pek stiker (200), Pizza party dengan kawan (300), Jalan-jalan ke taman (500).',
          tip: isEN ? 'Let your child choose from a "reward menu" — it teaches decision making!' : 'Biar anak pilih dari "menu ganjaran" — ini ajar membuat keputusan!',
        },
        {
          icon: '🏫',
          title: isEN ? 'School Rewards (Teachers)' : 'Ganjaran Sekolah (Guru)',
          desc: isEN
            ? 'Reward students who earn the most coins! Ideas: Homework pass - skip 1 (100 coins), VIP seat for a day (50), Teacher\'s helper for a day (80), Extra recess 15 min (150), Treasure box pick (120), Special certificate (60).'
            : 'Beri ganjaran kepada murid yang paling banyak syiling! Idea: Pass homework - skip 1 (100 syiling), Kerusi VIP sehari (50), Pembantu guru sehari (80), Rehat tambahan 15 min (150), Pilih dari kotak harta (120), Sijil khas (60).',
          tip: isEN ? 'Announce top coin earners during assembly for extra motivation!' : 'Umumkan pelajar dengan syiling tertinggi semasa perhimpunan untuk motivasi!',
        },
        {
          icon: '🏆',
          title: isEN ? 'Competition Rewards' : 'Ganjaran Pertandingan',
          desc: isEN
            ? 'For class tournaments and weekly leaderboard champions: Small trophies or medals (buy from Shopee RM2-5 each), Print certificates using PANTASS Certificate feature, Name on "Hall of Fame" board in class, Extra marks or bonus points, Class party for the winning team.'
            : 'Untuk pertandingan kelas dan juara papan markah mingguan: Trofi atau medal kecil (beli dari Shopee RM2-5), Cetak sijil guna feature Sijil PANTASS, Nama di papan "Dewan Kegemilangan", Markah bonus, Parti kelas untuk pasukan menang.',
          tip: isEN ? 'Weekly leaderboard resets every Monday — everyone gets a fresh chance!' : 'Papan markah mingguan reset setiap Isnin — semua dapat peluang baru!',
        },
      ],
    },
    {
      id: 'affiliate',
      icon: HandCoins,
      color: '#009688',
      title: isEN ? 'Affiliate Program (Teachers)' : 'Program Affiliate (Guru)',
      subtitle: isEN ? 'Earn 30% recurring commission' : 'Dapat 30% komisen berulang',
      steps: [
        {
          icon: '💰',
          title: isEN ? 'How It Works' : 'Cara Berfungsi',
          desc: isEN
            ? 'Join for free and get a unique referral link. Share it with parents. When they subscribe to any paid plan, you earn 30% of their subscription — every month! No limit on earnings.'
            : 'Sertai percuma dan dapat link rujukan unik. Kongsi kepada ibu bapa. Bila mereka langgan mana-mana pelan berbayar, anda dapat 30% daripada langganan mereka — setiap bulan! Tiada had pendapatan.',
          tip: isEN ? '10 parents on Basic = RM59.70/month passive income!' : '10 ibu bapa pelan Asas = RM59.70/bulan pendapatan pasif!',
        },
        {
          icon: '📊',
          title: isEN ? 'Track Your Income' : 'Jejak Pendapatan Anda',
          desc: isEN
            ? 'Your Affiliate Dashboard shows: total signups, paid subscribers, total earnings, daily income breakdown by date, and monthly income filter. See exactly how much you earn each day!'
            : 'Dashboard Affiliate anda tunjuk: jumlah pendaftaran, pelanggan berbayar, jumlah pendapatan, pecahan pendapatan harian, dan penapis pendapatan bulanan. Lihat tepat berapa anda jana setiap hari!',
          tip: isEN ? 'Use the monthly filter to compare income across months!' : 'Guna penapis bulanan untuk banding pendapatan antara bulan!',
        },
        {
          icon: '🧮',
          title: isEN ? 'Income Calculator' : 'Kalkulator Pendapatan',
          desc: isEN
            ? 'Use the built-in Calculator tab to project your potential income. Select a plan (Basic/Pro/School), slide the number of parents, and instantly see your monthly and yearly earnings. Try the quick examples: 10, 50, 100, or 200 parents!'
            : 'Guna tab Kalkulator untuk unjurkan potensi pendapatan. Pilih pelan (Asas/Pro/Sekolah), geser bilangan ibu bapa, dan terus lihat pendapatan bulanan dan tahunan. Cuba contoh pantas: 10, 50, 100, atau 200 ibu bapa!',
          tip: isEN ? '200 parents on Basic = RM1,194/month!' : '200 ibu bapa pelan Asas = RM1,194/bulan!',
        },
        {
          icon: '📈',
          title: isEN ? 'Analytics & Milestones' : 'Analitik & Milestone',
          desc: isEN
            ? 'The Analytics tab shows visual charts: Conversion Rate (donut), Subscriber Plans (pie), Commission Per Plan (bar), Income Growth Projection (area), and Monthly Earnings (bar). Plus income milestones to motivate you: Starter (RM100), Rising Star (RM500), Top Earner (RM1,000), Diamond (RM5,000)!'
            : 'Tab Analitik tunjuk carta visual: Kadar Penukaran (donut), Pelan Pelanggan (pie), Komisen Setiap Pelan (bar), Unjuran Pertumbuhan (area), dan Pendapatan Bulanan (bar). Tambah milestone pendapatan: Pemula (RM100), Bintang Naik (RM500), Penjana Utama (RM1,000), Berlian (RM5,000)!',
          tip: isEN ? 'Share your analytics with fellow teachers to inspire them!' : 'Kongsi analitik anda dengan guru lain untuk inspirasi mereka!',
        },
      ],
    },
    {
      id: 'teacher',
      icon: GraduationCap,
      color: '#009688',
      title: isEN ? 'For Teachers & Parents' : 'Untuk Guru & Ibu Bapa',
      subtitle: isEN ? 'Track progress & manage classes' : 'Jejak kemajuan & urus kelas',
      steps: [
        {
          icon: '👨‍🏫',
          title: isEN ? 'Class Management' : 'Pengurusan Kelas',
          desc: isEN
            ? 'Create classes, add students, and track their performance. See each student\'s games played, accuracy, and streak. Available on Pro and School plans.'
            : 'Buat kelas, tambah pelajar, dan jejak prestasi mereka. Lihat game dimainkan, ketepatan, dan streak setiap pelajar. Tersedia untuk pelan Pro dan School.',
          tip: isEN ? 'Export class reports as PDF to share with parents!' : 'Eksport laporan kelas sebagai PDF untuk kongsi dengan ibu bapa!',
        },
        {
          icon: '🏆',
          title: isEN ? 'Tournaments' : 'Pertandingan',
          desc: isEN
            ? 'Create elimination-style tournament brackets with up to 8 teams. Record match results and track the bracket to find the champion. School plan only.'
            : 'Buat bracket pertandingan gaya penyingkiran dengan sehingga 8 pasukan. Rekod keputusan dan jejak bracket untuk cari juara. Pelan School sahaja.',
          tip: isEN ? 'Great for classroom competitions and school events!' : 'Sesuai untuk pertandingan kelas dan acara sekolah!',
        },
        {
          icon: '📊',
          title: isEN ? 'Progress Tracking' : 'Penjejakan Kemajuan',
          desc: isEN
            ? 'View detailed stats: total matches, accuracy percentage, best streaks, and all achievements. Export everything as a PDF report.'
            : 'Lihat statistik terperinci: jumlah perlawanan, peratusan ketepatan, streak terbaik, dan semua pencapaian. Eksport semua sebagai laporan PDF.',
          tip: isEN ? 'Share progress reports with parents via WhatsApp!' : 'Kongsi laporan kemajuan dengan ibu bapa melalui WhatsApp!',
        },
      ],
    },
    {
      id: 'plans',
      icon: CreditCard,
      color: '#6366f1',
      title: isEN ? 'Plans & Pricing' : 'Pelan & Harga',
      subtitle: isEN ? 'Choose the right plan' : 'Pilih pelan yang sesuai',
      steps: [
        {
          icon: '🆓',
          title: isEN ? 'Free Plan' : 'Pelan Percuma',
          desc: isEN
            ? '10 games/month, 2 subjects (Math + BM), Easy difficulty only, World Leaderboard access. Perfect to try PANTASS!'
            : '10 permainan/bulan, 2 subjek (Matematik + BM), Tahap Mudah sahaja, Akses Papan Markah Dunia. Sesuai untuk cuba PANTASS!',
          tip: isEN ? 'All gamification features included — streaks, XP, coins, shop!' : 'Semua ciri gamifikasi termasuk — streak, XP, syiling, kedai!',
        },
        {
          icon: '💙',
          title: isEN ? 'Basic — RM19.90/month' : 'Asas — RM19.90/bulan',
          desc: isEN
            ? 'Unlimited games, all 8 subjects, Easy & Medium difficulty, full leaderboard, progress tracking & achievements.'
            : 'Permainan tanpa had, semua 8 subjek, Tahap Mudah & Sederhana, papan markah penuh, penjejakan kemajuan & pencapaian.',
          tip: isEN ? 'Save 20% with annual billing!' : 'Jimat 20% dengan pembayaran tahunan!',
        },
        {
          icon: '🔥',
          title: isEN ? 'Pro — RM49.90/month' : 'Pro — RM49.90/bulan',
          desc: isEN
            ? 'Everything in Basic + all difficulty levels (including Secondary), PDF reports, class management, priority support.'
            : 'Semua dalam Asas + semua tahap (termasuk Menengah), laporan PDF, pengurusan kelas, sokongan keutamaan.',
          tip: isEN ? 'Best for serious students who want to excel!' : 'Terbaik untuk pelajar serius yang nak cemerlang!',
        },
        {
          icon: '🏫',
          title: isEN ? 'School — RM199/month' : 'Sekolah — RM199/bulan',
          desc: isEN
            ? 'Everything in Pro + 50+ students, school branding, teacher dashboard, bulk reports, inter-class tournaments, custom questions, dedicated support.'
            : 'Semua dalam Pro + 50+ pelajar, branding sekolah, dashboard guru, laporan pukal, pertandingan antara kelas, soalan tersuai, sokongan khusus.',
          tip: isEN ? 'Only RM3.98/student/month for 50 students!' : 'Hanya RM3.98/pelajar/bulan untuk 50 pelajar!',
        },
      ],
    },
  ];

  return (
    <div className="dashboard-page">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} gData={gData} />

      <header className="dashboard-header">
        <button className="sidebar-toggle" onClick={() => setSidebarOpen(true)} data-testid="sidebar-toggle" aria-label="Open menu">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        <h1 className="dashboard-header-title">{isEN ? 'Guide' : 'Panduan'}</h1>
        <LanguageToggle className="dashboard-lang-toggle" />
      </header>

      <div className="dashboard-content">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          {/* Header */}
          <div className="guide-hero" data-testid="guide-hero">
            <div className="guide-hero-icon"><BookOpen size={32} /></div>
            <h2 className="guide-hero-title">
              {isEN ? 'Welcome to PANTASS!' : 'Selamat Datang ke PANTASS!'}
            </h2>
            <p className="guide-hero-desc">
              {isEN
                ? 'Learn how to play, earn rewards, and become a champion. Tap any section below to explore!'
                : 'Pelajari cara bermain, dapatkan ganjaran, dan jadilah juara. Tekan mana-mana bahagian di bawah!'}
            </p>
          </div>

          {/* Quick Start */}
          <div className="guide-quickstart" data-testid="guide-quickstart">
            <h3 className="guide-quickstart-title">
              <Zap size={18} /> {isEN ? 'Quick Start' : 'Mula Pantas'}
            </h3>
            <div className="guide-quickstart-steps">
              <div className="guide-qs-step" onClick={() => navigate('/')} data-testid="guide-qs-step-1">
                <div className="guide-qs-num">1</div>
                <div className="guide-qs-text">{isEN ? 'Pick a Subject' : 'Pilih Subjek'}</div>
                <ChevronRight size={16} />
              </div>
              <div className="guide-qs-step" onClick={() => navigate('/setup')} data-testid="guide-qs-step-2">
                <div className="guide-qs-num">2</div>
                <div className="guide-qs-text">{isEN ? 'Set Up Match' : 'Setup Perlawanan'}</div>
                <ChevronRight size={16} />
              </div>
              <div className="guide-qs-step" data-testid="guide-qs-step-3">
                <div className="guide-qs-num">3</div>
                <div className="guide-qs-text">{isEN ? 'Answer & Win!' : 'Jawab & Menang!'}</div>
                <Star size={16} color="#f59e0b" />
              </div>
            </div>
          </div>

          {/* Sections */}
          <div className="guide-sections" data-testid="guide-sections">
            {sections.map((section) => {
              const SectionIcon = section.icon;
              const isOpen = activeSection === section.id;

              return (
                <div key={section.id} className="guide-section" data-testid={`guide-section-${section.id}`}>
                  <button
                    className={`guide-section-header ${isOpen ? 'guide-section-open' : ''}`}
                    onClick={() => toggleSection(section.id)}
                    data-testid={`guide-section-toggle-${section.id}`}
                  >
                    <div className="guide-section-icon" style={{ background: section.color }}>
                      <SectionIcon size={20} color="white" />
                    </div>
                    <div className="guide-section-info">
                      <span className="guide-section-title">{section.title}</span>
                      <span className="guide-section-subtitle">{section.subtitle}</span>
                    </div>
                    <motion.div
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                      className="guide-section-arrow"
                    >
                      <ChevronDown size={20} />
                    </motion.div>
                  </button>

                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        className="guide-section-content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                      >
                        {section.steps.map((step, idx) => {
                          const stepKey = `${section.id}-${idx}`;
                          const stepOpen = activeStep[stepKey];

                          return (
                            <div key={idx} className="guide-step" data-testid={`guide-step-${section.id}-${idx}`}>
                              <button
                                className="guide-step-header"
                                onClick={() => toggleStep(section.id, idx)}
                              >
                                <span className="guide-step-icon">{step.icon}</span>
                                <span className="guide-step-title">{step.title}</span>
                                <motion.div animate={{ rotate: stepOpen ? 90 : 0 }} transition={{ duration: 0.15 }}>
                                  <ChevronRight size={16} color="#94a3b8" />
                                </motion.div>
                              </button>

                              <AnimatePresence>
                                {stepOpen && (
                                  <motion.div
                                    className="guide-step-body"
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.2 }}
                                  >
                                    <p className="guide-step-desc">{step.desc}</p>
                                    {step.tip && (
                                      <div className="guide-step-tip">
                                        <Gift size={14} />
                                        <span>{step.tip}</span>
                                      </div>
                                    )}
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          );
                        })}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>

          {/* CTA */}
          <div className="guide-cta" data-testid="guide-cta">
            <motion.button
              className="guide-cta-btn"
              onClick={() => navigate('/')}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <Gamepad2 size={20} />
              {isEN ? 'Start Playing Now!' : 'Mula Bermain Sekarang!'}
            </motion.button>
          </div>

          <button className="setup-back-btn" onClick={() => navigate('/')} style={{ marginTop: '16px' }}>
            &larr; {t('backToHome', language)}
          </button>
        </motion.div>
      </div>
    </div>
  );
}
